import type { LoaderFunction } from '@remix-run/node';
import { json } from '@remix-run/node';
import type { MetaFunction } from '@remix-run/react';
import { useLoaderData } from '@remix-run/react';
import { isPrefetch } from 'remix-utils/is-prefetch';
import type { CreditCardCategory } from '@cardo/types';
import CreditCard from '~/components/blocks/CreditCard';
import { DefaultErrorBoundary } from '@cardo/ui';
import { generateMetaFromSeoComponent } from '~/lib/meta';
import { fetchStrapiNoLimit } from '~/lib/server/api.server';
import type { V2_ErrorBoundaryComponent } from '@remix-run/react/dist/routeModules';

export const ErrorBoundary: V2_ErrorBoundaryComponent = DefaultErrorBoundary;

export const meta: MetaFunction = ({ data }) => {
  return [
    ...generateMetaFromSeoComponent(data?.attributes.seo),
    { title: 'Cardonomics - Credit Card Categories' },
  ];
};

export function headers({ loaderHeaders }: { loaderHeaders: Headers }) {
  return { 'cache-control': loaderHeaders.get('cache-control') };
}

export const loader: LoaderFunction = async ({ params, request }) => {
  const res = await fetchStrapiNoLimit<CreditCardCategory[]>(
    '/credit-card-categories',
    {
      populate: {
        creditCards: {
          populate: {
            blog: '*',
            editorial: {
              populate: {
                lines: '*',
              },
            },
            calculator: '*',
            cta: '*',
          },
        },
        seo: {
          populate: '*',
        },
      },
      filters: { slug: params.categorySlug },
    }
  );

  let headers = new Headers({
    'cache-control': 'max-age=1, s-maxage=600, stale-while-revalidate=31536000',
  });

  if (isPrefetch(request)) {
    headers.set('Cache-Control', 'private, max-age=10, s-maxage=0');
  }

  if (!res.data || res.data?.length === 0) {
    throw new Response('Not Found', { status: 404, headers });
  }

  return json(res.data[0], { headers });
};

export default function CardsCategory() {
  const category = useLoaderData() as CreditCardCategory;

  const creditCards = category?.attributes.creditCards.data;

  if (creditCards?.length === 0) {
    return (
      <p>
        Uh oh, looks like we don't have any credit cards in this category right
        now.
      </p>
    );
  }

  return (
    <div className="mt-5 flex flex-col space-y-8">
      {creditCards.map((creditCard) => (
        <CreditCard
          id={creditCard.id}
          creditCard={{ data: creditCard }}
          key={creditCard.id}
        />
      ))}
    </div>
  );
}
